import { Environment, Url } from '@atlassiansox/bxpkit-core';
import { Storage } from '@atlassiansox/bxpkit-core';
import type { AnalyticsTrackType } from '@atlassiansox/bxpkit-core';
import OriginTracing from '@atlassiansox/origin-tracing';
import { SignupLayoutAnalyticsType } from '../../model';

/**
 * Analytics Singleton for Signup Layout
 */

export class SignupLayoutAnalytics {
  public static instance: SignupLayoutAnalytics;

  public instance?: SignupLayoutAnalytics;
  public isLoggedIn: boolean;
  public analyticsTrack: AnalyticsTrackType;
  public queryParameters: { [key: string]: string };
  public performanceStartTime?: number;
  public signInUserOrigin?: OriginTracing;
  public switchUserOrigin?: OriginTracing;
  public signInAccountUrl = '';
  public switchAccountUrl = '';
  public originAnalyticsAttributes?: any;

  public constructor({
    isLoggedIn,
    analyticsTrack,
    performanceStartTime
  }: SignupLayoutAnalyticsType) {
    this.isLoggedIn = isLoggedIn;
    this.analyticsTrack = analyticsTrack;
    this.performanceStartTime = performanceStartTime;
    this.queryParameters = Url.getUrlParams() || {};

    if (SignupLayoutAnalytics.instance) {
      // only use 1/first-created instance (this is a singleton)
      return SignupLayoutAnalytics.instance;
    }

    SignupLayoutAnalytics.instance = this;
    this._initOrigin();
    this._onloadAnalytics();
  }

  /**
   * Initialize Origin ID Tracing values
   */

  public _initOrigin() {
    this.signInUserOrigin = new OriginTracing({ product: 'wac-signup-login' });
    this.switchUserOrigin = new OriginTracing({ product: 'wac-signup-switch' });

    this.signInAccountUrl = Environment.getIdSigninUrl(
      this.signInUserOrigin.encode(),
      undefined,
      this.queryParameters['continue']
    );
    this.switchAccountUrl = Environment.getIdSwitchUserUrl(
      this.switchUserOrigin.encode()
    );

    this.originAnalyticsAttributes = {
      signInUser: this.signInUserOrigin.toAnalyticsAttributes({
        hasGeneratedId: true
      }),
      switchUser: this.switchUserOrigin.toAnalyticsAttributes({
        hasGeneratedId: true
      })
    };
  }

  /**
   * Events to fired when the component has mounted
   */

  public _onloadAnalytics() {
    const { segment, newRelic } = this.analyticsTrack;

    segment('bxp.try.signup.reactversionshown', {});
    newRelic.attribute('isReact', true);
    newRelic.attribute(
      'anonymous_id',
      (Storage.getCookie('ajs_anonymous_id') || '').replace(/"/g, '')
    );
    ['edition', 'product', 'bundle', 'coupon'].forEach((paramName) =>
      newRelic.attribute(
        paramName,
        this.queryParameters[paramName] || `no ${paramName}`
      )
    );

    // performance
    if (this.performanceStartTime) {
      const performanceDuration = performance.now() - this.performanceStartTime;
      segment('bxp.formCloudSignup.timeToFormReady', {
        performanceDuration,
        isLoggedIn: this.isLoggedIn
      });
      newRelic.attribute('isLoggedIn', this.isLoggedIn);
      newRelic.action('bxp.formCloudSignup.timeToFormReady', {
        performanceDuration,
        isLoggedIn: this.isLoggedIn
      });
    }
  }

  /**
   * Getters
   */

  public getOriginTraces() {
    return {
      signInAccountUrl: this.signInAccountUrl || '',
      switchAccountUrl: this.switchAccountUrl || '',
      originAnalyticsAttributes: this.originAnalyticsAttributes
    };
  }

  /**
   * Custom Events/Attributes
   */

  public setIsMigration(isMigration: boolean) {
    const { newRelic } = this.analyticsTrack;
    newRelic.attribute('isMigration', isMigration);
  }

  public trackBbSignupPerf() {
    const { segment } = this.analyticsTrack;
    if (this.performanceStartTime) {
      const performanceDuration = performance.now() - this.performanceStartTime;
      segment('bxp.formCloudSignup.timeToFormReadyBitbucketTeams', {
        performanceDuration,
        isLoggedIn: this.isLoggedIn
      });
    }
  }
}
